<template>
  <ContentTemplate
    :mainHeading="mainHeading"
    :subHeading="subHeading"
    :sections="sections"
    :showContentNav="true"
  />
</template>

<script>
import ContentTemplate from '@/components/ContentTemplate.vue';

export default {
  name: 'PrivacyPolicyContent',
  components: {
    ContentTemplate
  },
  data() {
    return {
      mainHeading: "Data Protection & Legal Information",
      subHeading: "As of: August 18, 2025",
      sections: [
        /* =========================================================================
         *  1) PRIVACY POLICY
         * ========================================================================= */
        {
          title: "Privacy Policy",
          ref: "privacy-policy",
          subsections: [
            {
              title: "1. Introduction",
              ref: "introduction",
              text: `
                <p>
                  The protection of your personal data is of particular concern to us, the Salzburg University of Applied Sciences,
                  as the operator of the website <strong>FoodCycle.ai</strong>. We treat your personal data confidentially and in
                  accordance with the statutory data protection regulations, in particular the General Data Protection Regulation
                  (GDPR) and the Austrian Data Protection Act (DSG).
                </p>
                <p>
                  With this privacy policy, we inform you about the nature, scope, and purpose of the processing of your personal data
                  in connection with the use of our website <strong>FoodCycle.ai</strong> and the associated services.
                </p>
              `
            },
            {
              title: "2. Controller",
              ref: "controller",
              text: `
                <p>The controller for data processing within the meaning of the GDPR is:</p>
                <p>
                  <strong>Salzburg University of Applied Sciences</strong><br/>
                  Urstein Süd 1<br/>
                  5412 Puch/Salzburg<br/>
                  Austria<br/>
                  Phone: +43 50 2211 0<br/>
                  E-Mail: <a href="mailto:office@fh-salzburg.ac.at" style="color: #87B140;">office@fh-salzburg.ac.at</a>
                </p>
                <p>
                  <strong>Data Protection Officer:</strong><br/>
                  Mag. Karolina Schempp, BSc, MSc<br/>
                  Urstein Süd 1, 5412 Puch/Salzburg<br/>
                  Tel: +43-(0)50-2211-0<br/>
                  E-Mail: <a href="mailto:datenschutz@fh-salzburg.ac.at" style="color: #87B140;">datenschutz@fh-salzburg.ac.at</a>
                </p>
              `
            },
            {
              title: "3. Data Processing when Using FoodCycle.ai",
              ref: "data-processing",
              subsections: [
                {
                  title: "3.1. User Management",
                  ref: "user-management",
                  text: `
                    <p>When registering on our website, we collect the following data:</p>
                    <ul>
                      <li>Username</li>
                      <li>Registered company</li>
                      <li>Name of the registered person</li>
                      <li>E-mail address</li>
                    </ul>
                    <p>
                      This data is used for user administration and authentication.
                      The legal basis for this processing is Art. 6 Para. 1 lit. b GDPR (contract fulfillment).
                      Authentication takes place using JWT tokens, which are stored in the browser and transmitted
                      to the server with each request.
                    </p>
                  `
                },
                {
                  title: "3.2. Form Entries",
                  ref: "form-entries",
                  text: `
                    <p>When using our tool for food surplus utilization, we collect the following data via form entries:</p>
                    <ul>
                      <li>Company address</li>
                      <li>Industry affiliation</li>
                      <li>Field of activity</li>
                      <li>Company name</li>
                      <li>Food surplus (type, quantity, frequency)</li>
                    </ul>
                    <p>
                      This data is processed in order to be able to offer you comprehensive solutions for the utilization
                      of your food surplus based on your entries. The legal basis for this is Art. 6 Para. 1 lit. b GDPR
                      (contract fulfillment). To create the solutions, this data is forwarded to our partner
                      <strong>vcg.ai</strong>.
                    </p>
                  `
                },
                {
                  title: "3.3. Chatbot Data",
                  ref: "chatbot-data",
                  text: `
                    <p>
                      Our chatbot, which is based on the OpenAI API, processes all text and questions entered by you.
                      In addition, the form entries (see 3.2) are also transmitted to the chatbot.
                    </p>
                    <p>
                      This data is processed in order to provide you with targeted support, analyze user problems,
                      optimize the chatbot, and ensure the functionality of the tool (e.g. restoring old conversations).
                      The legal basis for this is Art. 6 Para. 1 lit. f GDPR (legitimate interest in optimizing the chatbot
                      and user experience as well as ensuring the functionality of the tool).
                    </p>
                    <p>
                      The data is passed on to OpenAI via the API and stored there for analysis purposes,
                      tool optimization, and the restoration of conversations.
                    </p>
                  `
                },
                {
                  title: "3.4. Website Statistics Data",
                  ref: "website-statistics-data",
                  text: `
                    <p>We collect the following statistical data about the use of our website:</p>
                    <ul>
                      <li>Page views</li>
                      <li>Number of users/visitors (logins)</li>
                      <li>Geographical origin (on a country basis)</li>
                      <li>Session duration</li>
                      <li>Conversion rate (proportion of users who match a solution in the app)</li>
                      <li>Downloaded Environmental Impact reports</li>
                      <li>Errors (HTTP status codes)</li>
                      <li>Failed login attempts</li>
                      <li>Multiple accesses in a short time</li>
                    </ul>
                    <p>
                      This data is used for reporting purposes within the framework of our research project
                      and passed on to Google Analytics in order to generate general statistics about website usage.
                      The legal basis for this is Art. 6 Para. 1 lit. a GDPR (consent).
                    </p>
                    <p>
                      Error data (HTTP status codes, failed login attempts, multiple accesses) are used to ensure
                      system security (protection against DDoS attacks, brute force attacks, etc.).
                      The legal basis for this is Art. 6 Para. 1 lit. f GDPR (legitimate interest in ensuring IT security).
                    </p>
                  `
                }
              ]
            },
            {
              title: "4. Data Sharing",
              ref: "data-sharing",
              text: `
                <p>Your data will be passed on to the following recipients:</p>
                <ul>
                  <li><strong>vcg.ai:</strong> Form entries for creating solutions for food surplus utilization.</li>
                  <li><strong>OpenAI:</strong> Chatbot data and form entries for chatbot interactions and data reconciliation.</li>
                  <li><strong>Google Analytics:</strong> Website statistics data to analyze website usage.</li>
                </ul>
                <p>
                  The transfer to OpenAI and Google Analytics involves transferring your data to the USA.
                  The TADPF (Trans-Atlantic Data Privacy Framework) came into force on July 10, 2023,
                  which means that an adequacy decision with the USA has been in place since that point in time.
                  In this context, however, it is not possible for the Salzburg University of Applied Sciences
                  to rule out the risk that US authorities may gain access to your data for control and monitoring purposes.
                </p>
              `
            },
            {
              title: "5. Storage Period",
              ref: "storage-period",
              text: `
                <p>We only store your data for as long as is necessary to fulfill the respective purposes or if there are statutory retention periods.</p>
                <ul>
                  <li><strong>User management data:</strong> For the duration of the active registration.</li>
                  <li><strong>Form entries:</strong> For the duration of processing your request and finding a solution, as well as for documentation in the research project.</li>
                  <li><strong>Chatbot data:</strong> For the duration of the active conversation, as well as for documentation and analysis in the research project.</li>
                  <li><strong>Website statistics data:</strong> In accordance with the storage periods of Google Analytics (standard 26 months).</li>
                  <li><strong>Error data:</strong> Automatically deleted after 14 days.</li>
                </ul>
              `
            },
            {
              title: "6. Cookies",
              ref: "cookies",
              subsections: [
                {
                  title: "General Information",
                  ref: "cookies-general-info",
                  text: `
                    <p>
                      Our website uses so-called cookies. Cookies are information that is stored on your device with the help of the browser.
                      They are downloaded from your internet browser the first time you visit a website.
                      The next time you visit this website with the same device, the cookie and the information stored in it
                      are either sent back to the website that created it (first-party cookie) or sent to another website
                      to which it belongs (third-party cookie). This allows the website to recognize that you have already
                      been called up with this browser and, in some cases, varies the content displayed.
                    </p>
                    <p>
                      We use cookies to make our offer user-friendly and to ensure the functionality of the website.
                      Some cookies remain stored on your device until you delete them.
                      They enable us to recognize your browser the next time you visit.
                    </p>
                  `
                },
                {
                  title: "6.1. Necessary Cookies",
                  ref: "necessary-cookies",
                  text: `
                    <p>
                      These cookies are absolutely necessary for the functionality of the website
                      and cannot be deactivated in your systems. Absolutely necessary cookies guarantee
                      functions without which you cannot use this website as intended and serve to enable
                      the operation of a website.
                    </p>
                  `
                },
                {
                  title: "6.2. Statistics Cookies",
                  ref: "statistics-cookies",
                  text: `
                    <p>
                      Statistics cookies collect information about how the website is used in order to improve
                      its attractiveness, content, and functionality. Using pseudonymized data from website users,
                      the user flow can be analyzed and evaluated. This gives us the opportunity to optimize website
                      content and serves for reporting in the research project.
                    </p>
                  `
                },
                {
                  title: "6.3. Functional Cookies",
                  ref: "functional-cookies",
                  text: `
                    <p>
                      These cookies are used for the functionality of the chatbot.
                    </p>
                  `
                }
              ]
            },
            {
              title: "7. Your Rights",
              ref: "your-rights",
              text: `
                <p>
                  In connection with these data processing operations, you have the fundamental rights to information,
                  correction, deletion, restriction of processing, and data portability. If the data processing is
                  based on your consent (Art. 6 Para. 1 lit a GDPR), you can revoke this at any time, whereby the
                  lawfulness of the data processing carried out up to the revocation is not affected.
                </p>
                <p>
                  If you believe that the processing of your personal data is not in accordance with the legal provisions,
                  you also have the right to complain to the Austrian Data Protection Authority
                  (Barichgasse 40-42, 1030 Vienna, phone: +43 1 52 152-0, email: dsb@dsb.gv.at) about data processing.
                  Instead of the Austrian supervisory authority, you can also lodge your complaint with another
                  supervisory authority responsible for your place of residence.
                </p>
              `
            },
            {
              title: "8. Changes to the Privacy Policy",
              ref: "policy-changes",
              text: `
                <p>
                  We reserve the right to adapt this data protection declaration at any time
                  so that it always complies with current legal requirements or to implement changes
                  to our services in the data protection declaration, e.g. when introducing new services.
                  The new data protection declaration then applies to your next visit.
                </p>
              `
            },
            {
              title: "9. Status",
              ref: "status",
              text: `
                <p>
                  [Date: January 20, 2025]
                </p>
              `
            }
          ]
        },

        /* =========================================================================
         *  2) LEGAL NOTICE (IMPRINT)
         * ========================================================================= */
        {
          title: "Imprint (Legal Notice)",
          ref: "imprint",
          subsections: [
            {
              title: "Media Owner and Publisher",
              ref: "media-owner",
              text: `
                <p>
                  Salzburg University of Applied Sciences<br/>
                  <a href="https://www.alpine-space.eu/project/cefoodcycle/" target="_blank" style="color: #87B140;">Interreg Alpine Space CEFoodCycle</a><br/>
                  Urstein Süd 1<br/>
                  A-5412 Puch/Salzburg<br/>
                  Austria
                </p>
              `
            },
            {
              title: "Contact Information",
              ref: "contact-info",
              text: `
                <p>
                  E-Mail: foodcycleai@gmail.com <br/>
                  Web: <a href="https://www.fh-salzburg.ac.at" target="_blank" style="color: #87B140;">https://www.fh-salzburg.ac.at</a>
                </p>
                <p>
                  VAT ID: ATU 44554503<br/>
                  Commercial Register Number: FN 166054y<br/>
                  Commercial Register Court: Salzburg Regional Court
                </p>
              `
            },
            {
              title: "Supervisory Authority",
              ref: "supervisory-authority",
              text: `
                <p>
                  Agency for Quality Assurance and Accreditation Austria / Federal Ministry of Science and Research.<br/>
                  The Salzburg University of Applied Sciences is a member of the Austrian Conference of Universities of Applied Sciences.
                </p>
              `
            }
          ]
        },

        /* =========================================================================
         *  3) COOKIE CONSENT NOTICE
         * ========================================================================= */
        {
          title: "Cookie Consent Notice",
          ref: "cookie-consent",
          subsections: [
            {
              title: "What are cookies?",
              ref: "cookies-info",
              text: `
                <p>
                  Cookies are small data records that are stored on your device when you visit our website.
                  We differentiate between technically necessary cookies and non-necessary cookies
                  (e.g. marketing or statistics cookies).
                </p>
              `
            },
            {
              title: "Technically Necessary Cookies",
              ref: "technically-necessary-cookies",
              text: `
                <p>
                  These cookies are essential for the operation and basic functions of our website
                  (e.g. to ensure logins, sessions). They cannot be deactivated, otherwise smooth operation —
                  especially the use of the chatbot — cannot be guaranteed.
                </p>
                <ul>
                  <li>
                    <strong>OpenAI (Chatbot):</strong> The AI chatbot that can be used via our website
                    is based on a connection to OpenAI. In order for the chatbot to work and for you to be able
                    to use the central functionality of our web application at all, it is necessary to process
                    certain data and use cookie/token mechanisms. The legal basis for this is either a
                    (pre-)contractual relationship (Art. 6 Para. 1 lit. b GDPR) or our legitimate interest
                    (Art. 6 Para. 1 lit. f GDPR) in providing the core function of our application.
                  </li>
                </ul>
                <p>
                  Please note that when using the chatbot, data may be transferred to the USA
                  (see section "Third Country Transfers"). However, since the chatbot is essential for the
                  core functions of the tool, this processing takes place regardless of your cookie consent.
                </p>
              `
            },
            {
              title: "Non-necessary Cookies (Statistics & Marketing)",
              ref: "non-necessary-cookies",
              text: `
                <p>
                  We need your express consent (Art. 6 Para. 1 lit a GDPR) to use analysis and tracking cookies
                  (e.g. Google Analytics). This allows data such as IP address, usage behavior, and other information
                  about your device to be collected and, in some cases, transmitted to the USA.
                </p>
                <ul>
                  <li>
                    <strong>Google Analytics:</strong> Used to improve our website, analyze page views
                    and user interactions. Will only be activated if you agree in the cookie banner.
                  </li>
                </ul>
              `
            },
            {
              title: "Third Country Transfers",
              ref: "third-country-transfers",
              text: `
                <p>
                  Some services (e.g. OpenAI, Google Analytics) are provided by companies in the USA.
                  Although an adequacy decision is in place with the entry into force of the
                  Trans-Atlantic Data Privacy Framework (TADPF) on July 10, 2023, there is still a residual risk
                  that US authorities could access your data.
                </p>
                <ul>
                  <li>
                    <strong>Chatbot (OpenAI):</strong> Processing takes place on the basis of our legitimate interests
                    or (pre-)contractual necessity (Art. 6 Para. 1 lit b or lit f GDPR), since the chatbot is
                    an essential part of our web application.
                  </li>
                  <li>
                    <strong>Google Analytics:</strong> Transfer only with consent (Art. 6 Para. 1 lit a GDPR).
                  </li>
                </ul>
              `
            },
            {
              title: "Consent and Withdrawal",
              ref: "consent-withdrawal",
              text: `
                <ul>
                  <li>
                    Technically necessary cookies (including the functions required for the chatbot)
                    are set when the page is loaded and cannot be deactivated.
                  </li>
                  <li>
                    Non-necessary cookies (e.g. for Google Analytics) require your consent in the cookie banner.
                  </li>
                </ul>
                <p>
                  You can change these settings at any time or withdraw your consent by clicking on the
                  "Cookie Settings" link (e.g. in the footer) or manually deleting cookies in your browser.
                  Please note that a withdrawal of consent does not affect the lawfulness of processing
                  carried out before the withdrawal.
                </p>
                <p>
                  <strong>Note:</strong> If you want to edit your cookie settings later,
                  you can reopen the cookie banner using the following link:
                  <br/>
                  <a href="#" onclick="CCM.openWidget(); return false;" style="color: #87B140;">Open cookie settings</a>
                </p>
              `
            },
            {
              title: "Blocking or Deleting Cookies",
              ref: "blocking-cookies",
              text: `
                <p>
                  If you generally want to block or delete cookies, you can do so in your browser settings.
                  Please note, however, that functions of our website — especially the chatbot —
                  may only function to a limited extent or not at all.
                </p>
                <p><strong>(As of: August 2025)</strong></p>
              `
            }
          ]
        }
      ]
    };
  },
  mounted() {
    // Emit sections so a parent component can process them
    this.$emit('sections-content-updated', this.sections);
  }
};
</script>
